import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Home } from 'lucide-react';
import { GroupList } from '@/components/GroupList';
import { ChatWindow } from '@/components/ChatWindow';
import { SalonControl } from '@/components/SalonControl';
import api, { getChatNotificationSummary, getGroup } from '@/lib/api';
import { getMutedTextColor, getPanelStyle, getSoftSurface, isDarkTheme, pickTextColor, withAlpha } from '@/lib/themeStyles';

export function SalonPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedGroup, setSelectedGroup] = useState<any>(null);
    const [user, setUser] = useState<any>(null);
    const [activeTab, setActiveTab] = useState<'salon' | 'control'>('salon');
    const [notificationSummary, setNotificationSummary] = useState<any>(null);
    const [jumpMessageId, setJumpMessageId] = useState<number | null>(null);
    const [jumpGroupId, setJumpGroupId] = useState<number | null>(null);
    const [isMobile, setIsMobile] = useState(false);
    const [mobileView, setMobileView] = useState<'list' | 'chat'>('list');
    const historyGuardRef = useRef(false);
    const [loadingUser, setLoadingUser] = useState(true);
    const [userLoadError, setUserLoadError] = useState('');

    const fetchCurrentUser = useCallback(async () => {
        setLoadingUser(true);
        setUserLoadError('');
        try {
            const res = await api.get('/users/me');
            setUser(res.data);
        } catch (err) {
            console.error(err);
            setUser(null);
            setUserLoadError('サロン情報の取得に失敗しました。再読み込みしてください。');
        } finally {
            setLoadingUser(false);
        }
    }, []);

    useEffect(() => {
        fetchCurrentUser();
    }, [fetchCurrentUser]);

    useEffect(() => {
        if (!user || !user.salon_enabled) return;
        const rawGroupId = searchParams.get('group_id');
        if (!rawGroupId) return;

        const groupId = Number(rawGroupId);
        const rawMessageId = searchParams.get('message_id');
        const messageId = Number(rawMessageId);
        const hasMessageId = Number.isFinite(messageId) && messageId > 0;

        const clearSearch = () => {
            const next = new URLSearchParams(searchParams);
            next.delete('group_id');
            next.delete('message_id');
            setSearchParams(next, { replace: true });
        };

        if (!Number.isFinite(groupId) || groupId <= 0) {
            clearSearch();
            return;
        }

        let cancelled = false;
        const jumpToTarget = async () => {
            try {
                const res = await getGroup(groupId);
                if (cancelled || !res.data) return;
                setSelectedGroup(res.data);
                setJumpGroupId(groupId);
                if (hasMessageId) {
                    setJumpMessageId(messageId);
                }
                if (isMobile) {
                    setMobileView('chat');
                }
            } catch (err) {
                console.error(err);
            } finally {
                if (!cancelled) {
                    clearSearch();
                }
            }
        };

        jumpToTarget();
        return () => {
            cancelled = true;
        };
    }, [user, isMobile, searchParams, setSearchParams]);

    const fetchNotifications = async () => {
        try {
            const res = await getChatNotificationSummary();
            setNotificationSummary(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        if (!user || !user.salon_enabled) return;
        fetchNotifications();
        const interval = setInterval(() => {
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
            fetchNotifications();
        }, 60000);
        const handleVisibility = () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
                fetchNotifications();
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [user]);

    useEffect(() => {
        const update = () => setIsMobile(window.innerWidth < 768);
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    useEffect(() => {
        if (!isMobile) {
            setMobileView('list');
            return;
        }
        if (!selectedGroup) {
            setMobileView('list');
        }
    }, [isMobile, selectedGroup]);

    useEffect(() => {
        if (!isMobile) {
            historyGuardRef.current = false;
            return;
        }
        if (mobileView === 'chat' && !historyGuardRef.current) {
            window.history.pushState({ salonView: 'chat' }, '', window.location.href);
            historyGuardRef.current = true;
        }
    }, [isMobile, mobileView]);

    useEffect(() => {
        const handlePopState = () => {
            if (!isMobile) return;
            if (mobileView === 'chat') {
                setMobileView('list');
                historyGuardRef.current = false;
                window.history.pushState({ salonView: 'list' }, '', window.location.href);
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [isMobile, mobileView]);

    // @ts-ignore
    const { theme } = useOutletContext<{ theme: any }>();

    const darkTheme = isDarkTheme(theme);
    const panelStyle = getPanelStyle(theme, 0.96, 0.9);
    const mutedTextColor = getMutedTextColor(theme);

    if (loadingUser) return <div>Loading...</div>;
    if (!user) {
        return (
            <div className="h-full flex flex-col items-center justify-center gap-3">
                <div className="text-sm" style={{ color: mutedTextColor }}>{userLoadError || 'サロン情報を読み込めませんでした。'}</div>
                <button
                    type="button"
                    onClick={fetchCurrentUser}
                    className="rounded px-3 py-2 text-xs font-semibold"
                    style={{
                        backgroundColor: theme?.colors?.primary || '#111111',
                        color: pickTextColor(theme?.colors?.primary || '#111111'),
                    }}
                >
                    再読み込み
                </button>
            </div>
        );
    }
    const canControl = ['Admin', 'Manager', 'Maestro'].includes(user.role_type) || user.username === 'TestMaster';
    const showList = !isMobile || mobileView === 'list';
    const showChat = !isMobile || mobileView === 'chat';

    return (
        <div className="flex flex-col h-full min-h-0 w-full overflow-hidden"
            style={{ backgroundColor: theme?.colors?.background || '#fff', color: theme?.colors?.text || '#000' }}>
            {canControl && (
                <div
                    className="flex flex-col gap-3 border-b px-3 py-3 md:flex-row md:items-center md:justify-between"
                    style={{
                        ...panelStyle,
                        borderColor: withAlpha(theme?.colors?.border || '#000000', 0.7),
                    }}
                >
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            className="flex items-center gap-1 rounded-full border px-3 py-2 text-sm font-semibold"
                            onClick={() => navigate(-1)}
                            type="button"
                            style={{
                                color: theme?.colors?.text,
                                borderColor: theme?.colors?.border,
                                backgroundColor: getSoftSurface(theme, 0.84, 0.46),
                            }}
                        >
                            <ArrowLeft size={16} />
                            <span>戻る</span>
                        </button>
                        <button
                            className="flex items-center gap-1 rounded-full border px-3 py-2 text-sm font-semibold"
                            onClick={() => navigate('/')}
                            type="button"
                            style={{
                                color: theme?.colors?.text,
                                borderColor: theme?.colors?.border,
                                backgroundColor: getSoftSurface(theme, 0.84, 0.46),
                            }}
                        >
                            <Home size={16} />
                            <span>自室へ</span>
                        </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            className="rounded-full border px-4 py-2 text-sm font-semibold transition-colors"
                            onClick={() => setActiveTab('salon')}
                            style={{
                                color: activeTab === 'salon' ? theme?.colors?.primary : mutedTextColor,
                                borderColor: activeTab === 'salon' ? theme?.colors?.primary : theme?.colors?.border,
                                backgroundColor: activeTab === 'salon'
                                    ? withAlpha(theme?.colors?.primary || '#000000', darkTheme ? 0.18 : 0.1)
                                    : 'transparent',
                            }}
                        >
                            Salon
                        </button>
                        <button
                            className="rounded-full border px-4 py-2 text-sm font-semibold transition-colors"
                            onClick={() => setActiveTab('control')}
                            style={{
                                color: activeTab === 'control' ? theme?.colors?.primary : mutedTextColor,
                                borderColor: activeTab === 'control' ? theme?.colors?.primary : theme?.colors?.border,
                                backgroundColor: activeTab === 'control'
                                    ? withAlpha(theme?.colors?.primary || '#000000', darkTheme ? 0.18 : 0.1)
                                    : 'transparent',
                            }}
                        >
                            Control
                        </button>
                    </div>
                </div>
            )}

            {(!canControl || activeTab === 'salon') && (
                <>
                    {isMobile && (
                        <div
                            className="md:hidden flex gap-2 border-b px-3 py-3"
                            style={{
                                ...panelStyle,
                                borderColor: withAlpha(theme?.colors?.border || '#000000', 0.7),
                            }}
                        >
                            <button
                                className="flex-1 rounded-full border px-4 py-2 text-sm font-semibold"
                                onClick={() => setMobileView('list')}
                                style={{
                                    color: mobileView === 'list' ? theme?.colors?.primary : mutedTextColor,
                                    borderColor: mobileView === 'list' ? theme?.colors?.primary : theme?.colors?.border,
                                    backgroundColor: mobileView === 'list'
                                        ? withAlpha(theme?.colors?.primary || '#000000', darkTheme ? 0.18 : 0.1)
                                        : 'transparent',
                                }}
                            >
                                ルーム一覧
                            </button>
                            <button
                                className="flex-1 rounded-full border px-4 py-2 text-sm font-semibold"
                                disabled={!selectedGroup}
                                onClick={() => selectedGroup && setMobileView('chat')}
                                style={{
                                    color: mobileView === 'chat' ? theme?.colors?.primary : mutedTextColor,
                                    borderColor: mobileView === 'chat' ? theme?.colors?.primary : theme?.colors?.border,
                                    backgroundColor: mobileView === 'chat'
                                        ? withAlpha(theme?.colors?.primary || '#000000', darkTheme ? 0.18 : 0.1)
                                        : 'transparent',
                                    opacity: selectedGroup ? 1 : 0.5,
                                }}
                            >
                                チャット
                            </button>
                        </div>
                    )}
                    <div className={`flex flex-1 min-h-0 overflow-hidden ${isMobile ? 'flex-col' : ''}`}>
                        <div className={`${showList ? 'flex' : 'hidden'} md:flex w-full md:w-64 min-h-0`}>
                            <GroupList
                                onSelectGroup={(group) => {
                                    setSelectedGroup(group);
                                    if (isMobile) setMobileView('chat');
                                }}
                                selectedGroupId={selectedGroup?.group_id}
                                notifications={notificationSummary}
                                onRefreshNotifications={fetchNotifications}
                                onJumpToMessage={(groupId, messageId) => {
                                    setJumpGroupId(groupId);
                                    setJumpMessageId(messageId);
                                    if (isMobile) setMobileView('chat');
                                }}
                            />
                        </div>

                        <div className={`${showChat ? 'flex' : 'hidden'} flex-1 flex-col w-full min-h-0 overflow-hidden`}>
                            {selectedGroup && !selectedGroup.is_deleted ? (
                                <ChatWindow
                                    key={selectedGroup.group_id}
                                    group={selectedGroup}
                                    currentUser={user}
                                    jumpMessageId={jumpGroupId === selectedGroup.group_id ? jumpMessageId : null}
                                    onJumpComplete={() => setJumpMessageId(null)}
                                    onBackToList={isMobile ? () => setMobileView('list') : undefined}
                                    showBackButton={false}
                                />
                            ) : (
                                <div className="flex-1 flex items-center justify-center flex-col opacity-50"
                                    style={{ color: theme?.colors?.text || 'gray' }}>
                                    <div className="text-6xl mb-4">💬</div>
                                    <p>
                                        {selectedGroup?.is_deleted
                                            ? 'このグループは削除されました。'
                                            : 'サロン（チャットルーム）を選択してください'}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            {canControl && activeTab === 'control' && (
                <div className="flex-1 min-h-0">
                    <SalonControl theme={theme} />
                </div>
            )}
        </div>
    );
}
